import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Upload, ArrowLeft } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";

const SellerIndividualCreateProfileForm = ({
  onSubmit,
  isLoading,
  formData,
  onGoBack,
}) => {
  const { showError } = useToast();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const [eIdFrontImage, seteIdFrontImage] = useState(null);
  const [eIdBackImage, seteIdBackImage] = useState(null);
  const [tradeLicenseImage, setTradeLicenseImage] = useState(null);
  const [trnCertificateImage, setTrnCertificateImage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleFileChange = (setFile) => (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError(translate("auth.signup.fileSizeError"));
        return;
      }
      setFile(file);
    }
  };

  const handleFormSubmit = (data) => {
    // Clear previous validation errors
    setValidationErrors([]);

    // Validate required fields
    const requiredFields = {
      location: data.location,
      identificationNo: data.identificationNo,
      idExprDate: data.idExprDate,
      eIdFrontImage: eIdFrontImage,
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value)
      .map(([key]) => key);

    if (missingFields.length > 0) {
      // Show all missing fields
      const fieldNames = {
        location: translate("auth.signup.location"),
        identificationNo: translate("auth.signup.identificationNumber"),
        idExprDate: translate("auth.signup.idExpiryDate"),
        eIdFrontImage: translate("auth.signup.emiratesIdFront"),
      };

      const errors = missingFields.map((field) => fieldNames[field]);
      setValidationErrors(errors);
      return;
    }

    onSubmit({
      ...data,
      eIdFrontImage,
      eIdBackImage,
      tradeLicenseImage,
      trnCertificateImage,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className='w-full h-full'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Back Button */}
      <div className={`mb-6 ${isRTL ? "text-right" : ""}`}>
        <button
          type='button'
          onClick={onGoBack}
          className={`flex items-center text-[#F15A29] hover:text-[#d14a1f] font-medium transition-colors ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <ArrowLeft
            className={`w-5 h-5 ${isRTL ? "ml-2 rotate-180" : "mr-2"}`}
          />
          {translate("auth.signup.back")}
        </button>
      </div>

      <div className='space-y-3'>
        <h3 className='text-2xl capitalize md:text-[28px] font-bold mb-1 text-[#131313] text-center'>
          {translate("auth.signup.completeSellerProfile")}
        </h3>
        <p className='text-[#13131399] capitalize pb-6 text-sm md:text-[16px] text-center'>
          {translate("auth.signup.additionalDetailsSellerIndividual")}
        </p>

        {/* Validation Errors Display */}
        {validationErrors.length > 0 && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
            <h4 className='text-sm font-semibold text-red-800 mb-2'>
              {translate("auth.signup.completeRequiredFields")}
            </h4>
            <div className='space-y-1'>
              {validationErrors.map((error, index) => (
                <div
                  key={index}
                  className={`text-sm text-red-700 flex items-center ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
                >
                  <svg
                    className={`w-4 h-4 text-red-500 ${
                      isRTL ? "ml-2" : "mr-2"
                    }`}
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  </svg>
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {/* Location */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.location")}{" "}
            <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            placeholder={translate("auth.signup.locationPlaceholder")}
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border ${
              errors.location ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("location", {
              required: translate("auth.signup.locationRequired"),
            })}
          />
          {errors.location && (
            <p className='mt-1 text-sm text-red-500'>
              {errors.location.message}
            </p>
          )}
        </div>

        {/* VAT Number */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.vatNumber")}
          </label>
          <input
            type='number'
            placeholder={translate("auth.signup.vatNumberPlaceholder")}
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border no-arrows ${
              errors.vatNo ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("vatNo", {
              pattern: {
                value: /^[0-9]+$/,
                message: translate("auth.signup.vatNumberNumbersOnly"),
              },
            })}
          />
          {errors.vatNo && (
            <p className='mt-1 text-sm text-red-500'>{errors.vatNo.message}</p>
          )}
        </div>

        {/* Identification Number */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.emiratesIdPassport")}{" "}
            <span className='text-red-500'>*</span>
          </label>
          <input
            type='number'
            placeholder={translate(
              "auth.signup.identificationNumberPlaceholder"
            )}
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border no-arrows ${
              errors.identificationNo ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("identificationNo", {
              required: translate("auth.signup.identificationNumberRequired"),
              pattern: {
                value: /^[0-9-]+$/,
                message: translate("auth.signup.emiratesIdPattern"),
              },
            })}
          />
          {errors.identificationNo && (
            <p className='mt-1 text-sm text-red-500'>
              {errors.identificationNo.message}
            </p>
          )}
        </div>

        {/* ID Expiry Date */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.idExpiryDate")}{" "}
            <span className='text-red-500'>*</span>
          </label>
          <input
            type='date'
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border ${
              errors.idExprDate ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("idExprDate", {
              required: translate("auth.signup.idExpiryDateRequired"),
            })}
          />
          {errors.idExprDate && (
            <p className='mt-1 text-sm text-red-500'>
              {errors.idExprDate.message}
            </p>
          )}
        </div>
        {/* Emirates ID Front */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.emiratesIdFront")}{" "}
            <span className='text-red-500'>*</span>
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-4 text-center'>
            <input
              type='file'
              id='eIdFrontImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(seteIdFrontImage)}
              className='hidden'
              required
            />
            <label
              htmlFor='eIdFrontImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-6 h-6 text-gray-500 mb-2' />
              <span className='text-sm text-gray-500'>
                {translate("auth.signup.fileFormats")}
              </span>
              <button
                type='button'
                className='mt-2 px-4 py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors'
                onClick={() => document.getElementById("eIdFrontImage").click()}
              >
                {translate("auth.signup.browseFile")}
              </button>
              {eIdFrontImage && (
                <p className='mt-2 text-sm text-green-600'>
                  {eIdFrontImage.name}
                </p>
              )}
            </label>
          </div>
          {!eIdFrontImage && (
            <p className='mt-1 text-sm text-red-500'>
              {translate("auth.signup.emiratesIdFrontRequired")}
            </p>
          )}
        </div>

        {/* Emirates ID Back */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.emiratesIdBack")}
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-4 text-center'>
            <input
              type='file'
              id='eIdBackImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(seteIdBackImage)}
              className='hidden'
            />
            <label
              htmlFor='eIdBackImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-6 h-6 text-gray-500 mb-2' />
              <span className='text-sm text-gray-500'>
                {translate("auth.signup.fileFormats")}
              </span>
              <button
                type='button'
                className='mt-2 px-4 py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors'
                onClick={() => document.getElementById("eIdBackImage").click()}
              >
                {translate("auth.signup.browseFile")}
              </button>
              {eIdBackImage && (
                <p className='mt-2 text-sm text-green-600'>
                  {eIdBackImage.name}
                </p>
              )}
            </label>
          </div>
          {/* Back image optional - no required error */}
        </div>

        {/* Bank Name */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.bankName")}
          </label>
          <input
            type='text'
            placeholder={translate("auth.signup.bankNamePlaceholder")}
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border ${
              errors.bankName ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("bankName")}
          />
        </div>

        {/* Account Holder Number */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.accountHolder")}
          </label>
          <input
            type='text'
            placeholder={translate("auth.signup.accountHolderNamePlaceholder")}
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border ${
              errors.accountHolderName ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("accountHolderName")}
          />
        </div>

        {/* AccountNumber (Optional) */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.accountNumber")}
          </label>
          <input
            type='number'
            placeholder={translate("auth.signup.accountNumberPlaceholder")}
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border border-[#13131399] rounded-lg bg-white focus:outline-none no-arrows`}
            {...register("accountNumber", {
              pattern: {
                value: /^[0-9]+$/,
                message: translate("auth.signup.accountNumberNumbersOnly"),
              },
            })}
          />
          {errors.accountNumber && (
            <p className='mt-1 text-sm text-red-500'>
              {errors.accountNumber.message}
            </p>
          )}
        </div>

        {/* Account IBAN Number */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.iban")}
          </label>
          <input
            type='text'
            placeholder={translate("auth.signup.ibanPlaceholder")}
            className={`w-full ${
              isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
            } py-3 border ${
              errors.iban ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("iban")}
          />
        </div>

        {/* Account Trade License Image */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.tradeLicenseImage")}
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-4 text-center'>
            <input
              type='file'
              id='tradeLicenseImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(setTradeLicenseImage)}
              className='hidden'
            />
            <label
              htmlFor='tradeLicenseImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-6 h-6 text-gray-500 mb-2' />
              <span className='text-sm text-gray-500'>
                {translate("auth.signup.fileFormats")}
              </span>
              <button
                type='button'
                className='mt-2 px-4 py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors'
                onClick={() =>
                  document.getElementById("tradeLicenseImage").click()
                }
              >
                {translate("auth.signup.browseFile")}
              </button>
              {tradeLicenseImage && (
                <p className='mt-2 text-sm text-green-600'>
                  {tradeLicenseImage.name}
                </p>
              )}
            </label>
          </div>
        </div>

        {/* Account TRN Certificate Image */}
        <div className='mb-4'>
          <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
            {translate("auth.signup.trnCertificateImage")}
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-4 text-center'>
            <input
              type='file'
              id='trnCertificateImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(setTrnCertificateImage)}
              className='hidden'
            />
            <label
              htmlFor='trnCertificateImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-6 h-6 text-gray-500 mb-2' />
              <span className='text-sm text-gray-500'>
                {translate("auth.signup.fileFormats")}
              </span>
              <button
                type='button'
                className='mt-2 px-4 py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors'
                onClick={() =>
                  document.getElementById("trnCertificateImage").click()
                }
              >
                {translate("auth.signup.browseFile")}
              </button>
              {trnCertificateImage && (
                <p className='mt-2 text-sm text-green-600'>
                  {trnCertificateImage.name}
                </p>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Submit button */}
      <button
        type='submit'
        disabled={isLoading}
        className={`w-full border border-[#F15A29] bg-white text-primary-dark py-3 rounded-lg hover:bg-primary-dark hover:text-white transition-all duration-200 font-medium mb-4 ${
          isLoading ? "opacity-90 cursor-not-allowed" : ""
        }`}
      >
        {isLoading
          ? translate("auth.signup.submitting")
          : translate("auth.signup.completeSellerRegistration")}
      </button>
    </form>
  );
};

export default SellerIndividualCreateProfileForm;
